import type { AgentAccessGrantTarget } from "@common/schemas/domains/agentAccess/agent-access-grant.schema";
import type { McpConnection } from "@common/schemas/domains/mcp/mcp-connection.schema";
import { pluralizeWithCount } from "@common/helpers/string";

export function connectionPermissions(scopes: string[]): string {
    if (scopes.includes("mcp:write")) {
        return "Read & write";
    }
    if (scopes.includes("mcp:read")) {
        return "Read-only";
    }
    return "-";
}

export function connectionStatus(connection: McpConnection): string {
    return connection.revokedAt ? "Revoked" : "Active";
}

interface ConnectionTargetDisplay {
    label: string;
    /** Target names to list in a tooltip. Empty when the label already names them. */
    tooltipNames: string[];
}

/**
 * A single target is named inline. Several are summarised as a count, with the
 * names moved to a tooltip so the column stays readable. Targets the current
 * user cannot see (lost access, deleted workspace) still count towards the
 * label but have no name to list.
 */
export function connectionTargetDisplay(
    grantTarget: AgentAccessGrantTarget | null,
    nameById: Map<string, string>,
): ConnectionTargetDisplay {
    if (!grantTarget || grantTarget.type === "all") {
        return { label: "All workspaces", tooltipNames: [] };
    }

    const ids =
        grantTarget.type === "workspaces"
            ? grantTarget.workspaceIds
            : grantTarget.organizationIds;
    const noun =
        grantTarget.type === "workspaces" ? "workspace" : "organization";

    const names: string[] = [];
    for (const id of ids) {
        const name = nameById.get(id);
        if (name) {
            names.push(name);
        }
    }

    if (ids.length === 1 && names.length === 1) {
        return { label: names[0], tooltipNames: [] };
    }

    return {
        label: pluralizeWithCount(noun, ids.length),
        tooltipNames: names,
    };
}
